import React from "react";
import { Text, View } from "react-native";
import { NormalText } from "../../../common/native";
import { ADMIN_TOKENS } from "../../../../styles/adminTokens";

type DirectLinkFormPreviewProps = {
  currentView: "Desktop" | "Mobile";
  formPreviewUrl: string;
  isFetchingSettings: boolean;
  shouldRender: boolean;
};

type PreviewState = "idle" | "loading" | "ready" | "error";

const DirectLinkFormPreview: React.FC<DirectLinkFormPreviewProps> = ({
  currentView,
  formPreviewUrl,
  isFetchingSettings,
  shouldRender,
}) => {
  const [previewState, setPreviewState] = React.useState<PreviewState>("idle");

  React.useEffect(() => {
    setPreviewState(shouldRender && formPreviewUrl ? "loading" : "idle");
  }, [formPreviewUrl, shouldRender]);

  const canRenderForm = shouldRender && Boolean(formPreviewUrl);
  const isDesktopView = currentView === "Desktop";

  return (
    <View
      style={{
        width: "100%",
        maxWidth: isDesktopView
          ? ADMIN_TOKENS.size.contentMaxWidth
          : ADMIN_TOKENS.size.eventCardMaxWidth,
        alignSelf: "flex-start",
        borderWidth: ADMIN_TOKENS.borderWidth.sm,
        borderColor: ADMIN_TOKENS.color.border,
        borderRadius: ADMIN_TOKENS.radius.xl,
        backgroundColor: ADMIN_TOKENS.color.surface,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          paddingVertical: ADMIN_TOKENS.spacing.sm,
          paddingHorizontal: ADMIN_TOKENS.spacing.md,
          borderBottomWidth: ADMIN_TOKENS.borderWidth.sm,
          borderBottomColor: ADMIN_TOKENS.color.border,
          backgroundColor: ADMIN_TOKENS.color.surfaceMuted,
        }}
      >
        <NormalText
          text="Lead capture form"
          fontWeight="600"
          style={{ color: ADMIN_TOKENS.color.textPrimary }}
        />
      </View>
      <View
        testID="direct-link-form-preview-frame"
        style={{
          position: "relative",
          width: "100%",
          height: ADMIN_TOKENS.size.formPreviewHeight,
          backgroundColor: ADMIN_TOKENS.color.background,
        }}
      >
        {canRenderForm ? (
          <iframe
            src={formPreviewUrl}
            title="Lead capture form preview"
            allow="autoplay"
            allowFullScreen
            sandbox="allow-forms allow-same-origin allow-scripts"
            onLoad={() => setPreviewState("ready")}
            onError={() => setPreviewState("error")}
            style={{
              position: "absolute",
              border: 0,
              width: "100%",
              height: "100%",
            }}
          >
            Browser does not support iframe
          </iframe>
        ) : null}

        {previewState !== "ready" ? (
          <View
            pointerEvents="none"
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              alignItems: "center",
              justifyContent: "center",
              padding: ADMIN_TOKENS.spacing.xl,
              backgroundColor: ADMIN_TOKENS.color.background,
            }}
          >
            <Text
              accessibilityLiveRegion="polite"
              style={{
                fontFamily: ADMIN_TOKENS.font.medium,
                fontSize: ADMIN_TOKENS.fontSize.sm,
                lineHeight: ADMIN_TOKENS.lineHeight.sm,
                color:
                  previewState === "error"
                    ? ADMIN_TOKENS.color.errorText
                    : ADMIN_TOKENS.color.textMuted,
                textAlign: "center",
              }}
            >
              {previewState === "error"
                ? "The form preview could not load. Refresh the page and try again."
                : previewState === "loading" || isFetchingSettings
                  ? "Loading form preview..."
                  : "Add a clinic SDK key to load the form preview."}
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );
};

export default DirectLinkFormPreview;
